import React, { useMemo, useState } from "react";
import { FiUser, FiMail, FiLock, FiSave, FiCheckCircle, FiAlertTriangle, FiEye, FiEyeOff } from "react-icons/fi";

export default function Settings() {
  // Profile state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  // Security state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // UI state
  const [profileMsg, setProfileMsg] = useState({ type: "", text: "" }); // success | error | ""
  const [securityMsg, setSecurityMsg] = useState({ type: "", text: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const emailValid = useMemo(() => {
    const v = String(email || "").trim();
    // simple, practical email check
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [email]);

  const newPasswordStrength = useMemo(() => {
    const v = String(newPassword || "");
    const score =
      (v.length >= 8 ? 1 : 0) +
      (/[A-Z]/.test(v) ? 1 : 0) +
      (/[a-z]/.test(v) ? 1 : 0) +
      (/\d/.test(v) ? 1 : 0) +
      (/[^A-Za-z0-9]/.test(v) ? 1 : 0);

    if (!v) return { label: "-", hint: "Enter a new password", ok: false, score: 0 };
    if (score <= 2) return { label: "Weak", hint: "Use 8+ chars, number & symbol", ok: false, score };
    if (score === 3) return { label: "Medium", hint: "Add uppercase or symbol", ok: true, score };
    return { label: "Strong", hint: "Good password", ok: true, score };
  }, [newPassword]);

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    setProfileMsg({ type: "", text: "" });

    const name = fullName.trim();
    const mail = email.trim();

    if (!name) {
      setProfileMsg({ type: "error", text: "Full name is required." });
      return;
    }
    if (!mail || !emailValid) {
      setProfileMsg({ type: "error", text: "Please enter a valid email address." });
      return;
    }

    // ✅ Later: send data to backend
    setProfileMsg({ type: "success", text: "Profile updated successfully (UI only)." });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setSecurityMsg({ type: "", text: "" });

    if (!currentPassword || !newPassword) {
      setSecurityMsg({ type: "error", text: "Please fill in both password fields." });
      return;
    }
    if (newPassword.length < 8) {
      setSecurityMsg({ type: "error", text: "New password must be at least 8 characters." });
      return;
    }
    if (currentPassword === newPassword) {
      setSecurityMsg({ type: "error", text: "New password must be different from current password." });
      return;
    }

    // ✅ Later: call backend API for password update
    setSecurityMsg({ type: "success", text: "Password updated successfully (UI only)." });
    setCurrentPassword("");
    setNewPassword("");
    setShowCurrent(false);
    setShowNew(false);
  };

  const Banner = ({ type, text }) => {
    if (!type || !text) return null;
    const isSuccess = type === "success";
    return (
      <div
        className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${
          isSuccess
            ? "bg-green-50 border-green-200 text-green-800"
            : "bg-red-50 border-red-200 text-red-800"
        }`}
      >
        <div className="mt-0.5">{isSuccess ? <FiCheckCircle /> : <FiAlertTriangle />}</div>
        <div>{text}</div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage your account profile and security settings.
            </p>
          </div>

          
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Profile */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
                  <FiUser />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Profile</h2>
                  <p className="text-xs text-gray-500">Update your name and email</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <Banner type={profileMsg.type} text={profileMsg.text} />

              <form className="space-y-4" onSubmit={handleProfileSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <div className="mt-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-400">
                    <FiUser className="text-gray-400" />
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter your full name"
                      className="w-full outline-none text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <div className="mt-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-400">
                    <FiMail className="text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full outline-none text-sm"
                    />
                  </div>

                  <div className="mt-2 text-xs">
                    {email ? (
                      emailValid ? (
                        <span className="text-green-700">✓ Email looks valid</span>
                      ) : (
                        <span className="text-red-700">Please enter a valid email format</span>
                      )
                    ) : (
                      <span className="text-gray-500">We’ll use this for notifications and recovery.</span>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  <FiSave /> Save Changes
                </button>
              </form>
            </div>
          </div>

          {/* Security */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
                  <FiLock />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Security</h2>
                  <p className="text-xs text-gray-500">Change your password</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <Banner type={securityMsg.type} text={securityMsg.text} />

              <form className="space-y-4" onSubmit={handlePasswordSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Password</label>
                  <div className="mt-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-emerald-400">
                    <FiLock className="text-gray-400" />
                    <input
                      type={showCurrent ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="w-full outline-none text-sm"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent((v) => !v)}
                      className="text-gray-500 hover:text-gray-700"
                      aria-label="Toggle current password visibility"
                    >
                      {showCurrent ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <div className="mt-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-emerald-400">
                    <FiLock className="text-gray-400" />
                    <input
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full outline-none text-sm"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((v) => !v)}
                      className="text-gray-500 hover:text-gray-700"
                      aria-label="Toggle new password visibility"
                    >
                      {showNew ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                    <div className="text-gray-600">{newPasswordStrength.hint}</div>
                    <div
                      className={`font-semibold ${
                        newPasswordStrength.label === "Strong"
                          ? "text-green-700"
                          : newPasswordStrength.label === "Medium"
                          ? "text-amber-700"
                          : newPasswordStrength.label === "Weak"
                          ? "text-red-700"
                          : "text-gray-500"
                      }`}
                    >
                      {newPasswordStrength.label}
                    </div>
                  </div>

                  {/* Strength bar */}
                  <div className="mt-2 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        newPasswordStrength.label === "Strong"
                          ? "bg-green-500"
                          : newPasswordStrength.label === "Medium"
                          ? "bg-amber-500"
                          : newPasswordStrength.label === "Weak"
                          ? "bg-red-500"
                          : "bg-gray-200"
                      }`}
                      style={{ width: `${Math.min(100, (newPasswordStrength.score / 5) * 100)}%` }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition"
                >
                  <FiSave /> Update Password
                </button>
              </form>

              <div className="text-xs text-gray-500 pt-2 border-t">
                Recommended: 8+ characters, mix of uppercase, lowercase, number, and symbol.
              </div>
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
}